import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { gamificationService } from '@/services/gamification.service';
import i18n from '@/i18n';

interface GamificationEventData {
  lessonId?: string;
  moduleId?: string;
  courseId?: string;
  quizScore?: number;
  exerciseId?: string;
  commentId?: string;
  projectId?: string;
  minutesStudied?: number;
  metadata?: Record<string, any>;
}

interface GamificationReward {
  xpEarned: number;
  totalXp: number;
  levelUp?: {
    oldLevel: any;
    newLevel: any;
    rewards?: {
      badges: string[];
      xpBonus: number;
      specialRewards: string[];
    };
  };
  badgeUnlocked?: {
    id: string;
    name: string;
    description: string;
    icon: string;
    rarity: 'common' | 'rare' | 'epic' | 'legendary';
    points: number;
    unlockedAt: Date;
    unlockMessage?: string;
    category: {
      name: string;
      icon: string;
      color: string;
    };
  };
  streakUpdated?: {
    currentStreak: number;
    longestStreak: number;
    milestoneReached?: number;
  };
}

interface UseGamificationOptions {
  enableNotifications?: boolean;
  enableLevelUpModal?: boolean;
  enableBadgeModal?: boolean;
  enableStreakTracking?: boolean;
}

export function useGamification(options: UseGamificationOptions = {}) {
  const {
    enableNotifications = true,
    enableLevelUpModal = true,
    enableBadgeModal = true,
    enableStreakTracking = true,
  } = options;

  const queryClient = useQueryClient();
  const [showLevelUpModal, setShowLevelUpModal] = React.useState(false);
  const [showBadgeModal, setShowBadgeModal] = React.useState(false);
  const [levelUpData, setLevelUpData] = React.useState<any>(null);
  const [unlockedBadge, setUnlockedBadge] = React.useState<any>(null);

  // Track XP awards
  const awardXpMutation = useMutation({
    mutationFn: async (
      _data: GamificationEventData & { actionType: string },
    ) => {
      // This would be called by the backend when actions happen
      // For now, we'll simulate the XP award
      return { xpEarned: 10, totalXp: 100 }; // Placeholder
    },
    onSuccess: (reward: GamificationReward) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['gamification'] });

      // Show notifications
      if (enableNotifications && reward.xpEarned > 0) {
        toast.success(
          i18n.t('gamification:toasts.xpEarned', { xp: reward.xpEarned }),
          {
            description: i18n.t('gamification:toasts.xpEarnedDescription'),
          },
        );
      }

      // Handle level up
      if (reward.levelUp && enableLevelUpModal) {
        setLevelUpData(reward.levelUp);
        setShowLevelUpModal(true);
      }

      // Handle badge unlock
      if (reward.badgeUnlocked && enableBadgeModal) {
        setUnlockedBadge(reward.badgeUnlocked);
        setShowBadgeModal(true);
      }

      // Handle streak milestone
      if (reward.streakUpdated?.milestoneReached && enableNotifications) {
        toast.success(
          i18n.t('gamification:toasts.streakMilestone', {
            count: reward.streakUpdated.milestoneReached,
          }),
          {
            description: i18n.t(
              'gamification:toasts.streakMilestoneDescription',
            ),
          },
        );
      }
    },
  });

  // Update streak
  const updateStreakMutation = useMutation({
    mutationFn: (minutesStudied: number) =>
      gamificationService.updateStreak(minutesStudied),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['gamification', 'streak'] });

      if (enableNotifications) {
        toast.success(i18n.t('gamification:toasts.streakUpdated'), {
          description: i18n.t('gamification:toasts.consecutiveDays', {
            count: data.currentStreak,
          }),
        });
      }
    },
  });

  // Convenience methods for different actions
  const trackLessonComplete = React.useCallback(
    async (lessonId: string, minutesWatched?: number) => {
      if (enableStreakTracking && minutesWatched) {
        await updateStreakMutation.mutateAsync(minutesWatched);
      }

      // The actual XP award would be handled by the backend
      // This is just for updating local state
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
    },
    [updateStreakMutation, enableStreakTracking, queryClient],
  );

  const trackModuleComplete = React.useCallback(
    async (_moduleId: string) => {
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
    },
    [queryClient],
  );

  const trackCourseComplete = React.useCallback(
    async (_courseId: string) => {
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
    },
    [queryClient],
  );

  const trackQuizComplete = React.useCallback(
    async (_quizId: string, _score: number, _attempts: number) => {
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
    },
    [queryClient],
  );

  const trackExerciseComplete = React.useCallback(
    async (_exerciseId: string) => {
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
    },
    [queryClient],
  );

  const trackCommentPost = React.useCallback(
    async (_commentId: string) => {
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
    },
    [queryClient],
  );

  const trackProjectSubmit = React.useCallback(
    async (_projectId: string) => {
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
    },
    [queryClient],
  );

  const trackDailyLogin = React.useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: ['gamification'] });
  }, [queryClient]);

  const trackStudySession = React.useCallback(
    async (minutes: number) => {
      if (enableStreakTracking && minutes >= 1) {
        await updateStreakMutation.mutateAsync(minutes);
      }
    },
    [updateStreakMutation, enableStreakTracking],
  );

  const trackHelpColleague = React.useCallback(
    async (_helpType: 'comment' | 'answer' | 'review') => {
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
    },
    [queryClient],
  );

  return {
    // Tracking methods
    trackLessonComplete,
    trackModuleComplete,
    trackCourseComplete,
    trackQuizComplete,
    trackExerciseComplete,
    trackCommentPost,
    trackProjectSubmit,
    trackDailyLogin,
    trackStudySession,
    trackHelpColleague,

    // Modal state
    showLevelUpModal,
    setShowLevelUpModal,
    levelUpData,
    showBadgeModal,
    setShowBadgeModal,
    unlockedBadge,

    // Mutation state
    isAwarding: awardXpMutation.isPending,
    isUpdatingStreak: updateStreakMutation.isPending,

    // Direct access to mutations
    awardXp: awardXpMutation.mutate,
    updateStreak: updateStreakMutation.mutate,
  };
}

// Hook for real-time gamification notifications
export function useGamificationNotifications() {
  const queryClient = useQueryClient();

  React.useEffect(() => {
    // This could connect to a WebSocket or Server-Sent Events
    // to receive real-time gamification updates

    // For now, we'll just poll for updates periodically
    const interval = setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: ['gamification', 'notifications'],
      });
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [queryClient]);

  // Mock notification data - in real implementation, this would come from the server
  return {
    unreadCount: 0,
    notifications: [],
    markAsRead: () => {},
    markAllAsRead: () => {},
  };
}

// =====================
// REAL HOOKS (TanStack Query)
// =====================

// Admin: get gamification settings
export function useGamificationSettings() {
  return useQuery({
    queryKey: ['gamification', 'settings'],
    queryFn: () => gamificationService.getSettings(),
    staleTime: 60000,
  });
}

// Admin: update gamification settings
export function useUpdateGamificationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: any) => gamificationService.updateSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gamification', 'settings'] });
    },
  });
}

// Public: check if gamification is enabled
export function usePublicGamificationSettings() {
  return useQuery({
    queryKey: ['gamification', 'settings', 'public'],
    queryFn: () => gamificationService.getPublicSettings(),
    staleTime: 60000,
  });
}

// User: my points and level
export function useMyPoints() {
  return useQuery({
    queryKey: ['gamification', 'points', 'me'],
    queryFn: () => gamificationService.getMyPoints(),
    staleTime: 30000,
  });
}

// User: my rank position
export function useMyRank() {
  return useQuery({
    queryKey: ['gamification', 'leaderboard', 'me'],
    queryFn: () => gamificationService.getMyRank(),
    staleTime: 30000,
  });
}

// User: my achievements
export function useMyAchievements() {
  return useQuery({
    queryKey: ['gamification', 'achievements', 'me'],
    queryFn: () => gamificationService.getMyAchievements(),
    staleTime: 60000,
  });
}

// All levels
export function useLevels() {
  return useQuery({
    queryKey: ['gamification', 'levels'],
    queryFn: () => gamificationService.getAllLevels(),
    staleTime: 60000,
  });
}

// Leaderboard
export function useLeaderboard(options?: {
  limit?: number;
  page?: number;
  period?: 'all' | 'monthly' | 'weekly';
}) {
  const params = typeof options === 'object' ? options : {};
  return useQuery({
    queryKey: ['gamification', 'leaderboard', params],
    queryFn: () => gamificationService.getLeaderboard(params),
    staleTime: 30000,
  });
}

// Daily community checklist
export function useDailyChecklist() {
  return useQuery({
    queryKey: ['gamification', 'daily-checklist'],
    queryFn: () => gamificationService.getDailyChecklist(),
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

// Challenge progress tracking
export function useChallengeProgress() {
  const queryClient = useQueryClient();

  const trackChallengeAction = React.useCallback(
    async (_challengeId: number, _actionType: string) => {
      queryClient.invalidateQueries({
        queryKey: ['gamification', 'challenges'],
      });
    },
    [queryClient],
  );

  return { trackChallengeAction };
}

// Video player gamification integration
export function useVideoPlayerGamification(lessonId: string) {
  const { trackLessonComplete, trackStudySession } = useGamification();

  const trackVideoComplete = React.useCallback(
    async (duration: number) => {
      await trackLessonComplete(lessonId, Math.ceil(duration / 60));
    },
    [lessonId, trackLessonComplete],
  );

  const trackVideoProgress = React.useCallback(
    async (watchedTime: number, _totalTime: number) => {
      if (watchedTime > 0 && watchedTime % 300 === 0) {
        await trackStudySession(5);
      }
    },
    [trackStudySession],
  );

  return { trackVideoComplete, trackVideoProgress };
}
